<template>
    <div class="animated fadeIn">
        <div class="kgr-grid kgr-grid--full kgr-grid--medium-1of5 kgr-grid--gutters kgr-row">
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.type') }}</span>
                    <kgr-multi-select
                        v-model="filter.examTypes"
                        :options="clubExamTypes"
                        display="title"
                        searchable
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label class="kgr-inputGroup">
                    <span class="kgr-inputGroup__label">{{ $t('club.exam.sport') }}</span>
                    <kgr-multi-select
                        v-model="filter.examSports"
                        :options="sports"
                        display="name"
                        searchable
                    />
                </label>
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup__label">
                    {{ $t('Archivierte Prüfungen anzeigen') }}
                </div>
                <kgr-checkbox
                    v-model="filter.includeArchived"
                />
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup__label">
                    &nbsp;
                </div>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="loading"
                    @click="load(true)"
                >
                    {{ $t('Filtern') }}
                </button>
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup__label">
                    &nbsp;
                </div>
                <a
                    v-if="isCreatable"
                    class="addButton kgr-button kgr-button--accent"
                    :href="createPath"
                >
                    <kgr-icon
                        icon-class="add"
                        small
                        circle
                        highlight
                        class="kgr-button__icon kgr-button__icon--left"
                    />
                    {{ $t('Prüfung erstellen') }}
                </a>
            </div>
        </div>
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.title') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.type') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('club.exam.sport') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td colspan="4" style="text-align: center;">
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="clubExams.length === 0">
                    <td colspan="4" style="text-align: center;">
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="clubExam in clubExams"
                        :key="clubExam.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <span :class="clubExam.archived ? 'kgr-color kgr-color--gray' : ''">
                                {{ clubExam.title }}
                                <a
                                    class="kgr-link"
                                    :href="resultUrl(clubExam.id)"
                                >
                                    ({{ getCount(clubExam.id) }})
                                </a>
                            </span>
                        </td>
                        <td class="kgr-table__cell">
                            <span :class="clubExam.archived ? 'kgr-color kgr-color--gray' : ''">
                                {{ clubExam.clubExamType !== null ? clubExam.clubExamType.title : '' }}
                            </span>
                        </td>
                        <td class="kgr-table__cell">
                            <span :class="clubExam.archived ? 'kgr-color kgr-color--gray' : ''">
                                {{ clubExam.sport !== null ? clubExam.sport.name : '' }}
                            </span>
                        </td>
                        <td class="kgr-table__cell">
                            <button
                                v-if="clubExam.id && clubExam.isEditable"
                                class="kgr-table__action"
                                :title="clubExam.isEditable ? $t('Prüfung bearbeiten') : null"
                                @click="edit(clubExam.id)"
                            >
                                <kgr-icon-edit size="15" />
                            </button>
                            <button
                                v-if="isDeletable"
                                :disabled="!clubExam.isDeletable"
                                class="kgr-table__action"
                                :title="clubExam.isDeletable ? $t('Prüfung löschen') : null"
                                @click="remove(clubExam.id)"
                            >
                                <kgr-icon-delete
                                    :class="{ 'kgr-icon--light noPointer': !clubExam.isDeletable }"
                                />
                            </button>
                            <button
                                v-if="isEditable"
                                :disabled="clubExam.archived"
                                class="kgr-table__action"
                                :title="!clubExam.archived ? $t('Prüfung archivieren') : null"
                                @click="archive(clubExam.id, true)"
                            >
                                <kgr-icon
                                    icon-class="archive"
                                    :class="{ 'kgr-icon--light noPointer': clubExam.archived }"
                                />
                            </button>
                            <button
                                :disabled="!clubExam.archived"
                                class="kgr-table__action"
                                :title="clubExam.archived ? $t('Prüfung reaktivieren') : null"
                                @click="archive(clubExam.id, false)"
                            >
                                <kgr-icon
                                    icon-class="unarchive"
                                    :class="{ 'kgr-icon--light noPointer': !clubExam.archived }"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
    </div>
</template>

<script>
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubExamRepository from '../../../js/repository/club/ClubExamRepository';
import ClubExamTypeRepository from '../../../js/repository/club/ClubExamTypeRepository';
import Error from '../../../js/model/Error';
import Icon from '../../../components/common/icon/Icon.vue';
import IconDelete from '../../../components/common/icon/IconDelete.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import Loading from '../../../components/common/Loading.vue';
import MultiSelect from '../../../components/common/form/select/MultiSelect.vue';
import SportRepository from '../../../js/repository/SportsRepository';

export default {
    components: {
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-edit': IconEdit,
        'kgr-loader': Loading,
        'kgr-multi-select': MultiSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        createPath: {
            type: String,
            required: true,
        },
        editPath: {
            type: String,
            required: true,
        },
        resultPath: {
            type: String,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: false,
            clubExams: [],
            clubExamResultCounts: [],
            clubExamTypes: [],
            sports: [],
            filter: {
                examTypes: [],
                examSports: [],
                includeArchived: false,
            },
        };
    },
    computed: {
        filterParams() {
            return {
                examTypeIds: this.filter.examTypes.map((e) => e.id),
                sportIds: this.filter.examSports.map((e) => e.id),
                includeArchived: this.filter.includeArchived,
            };
        },
    },
    created() {
        this.load(true);
        this.loadResultCounts();
        this.loadExamTypes();
        this.loadSports();
    },
    methods: {
        load(setLoading) {
            this.loading = setLoading;
            ClubExamRepository.findByClubId(this.clubId, this.filterParams)
                .then((response) => this.clubExams = response)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        loadResultCounts() {
            ClubExamRepository.getResultCountByClubId(this.clubId)
                .then((response) => this.clubExamResultCounts = response);
        },
        loadExamTypes() {
            ClubExamTypeRepository.findByClubId(this.clubId)
                .then((data) => this.clubExamTypes = data)
                .catch(() => this.$notify.error('error.loadingData'));
        },
        loadSports() {
            SportRepository.findAll(this.clubId)
                .then((data) => this.sports = data)
                .catch(() => this.$notify.error('error.loadingData'));
        },
        edit(clubExamId) {
            window.location.href = this.editPath + clubExamId;
        },
        add() {
            window.location.href = this.createPath;
        },
        remove(clubExamId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Soll die Prüfung wirklich gelöscht werden?'), () => {
                ClubExamRepository.delete(clubExamId)
                    .then(() => {
                        this.load(false);
                        this.$notify.success('Die Prüfung wurde gelöscht.');
                    })
                    .catch(() => this.$notify.error('Die Prüfung konnte nicht gelöscht werden.'));
            });
        },
        archive(clubExamId, archive) {
            ClubExamRepository.archive(clubExamId, archive)
                .then(() => this.load(false))
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        getCount(clubExamId) {
            if (this.clubExamResultCounts) {
                const resultCount = this.clubExamResultCounts.filter((element) => element.clubExamId === clubExamId);
                if (resultCount.length === 1) {
                    return resultCount[0].count;
                }
            }
            return 0;
        },
        resultUrl(clubExamId) {
            return `${this.resultPath}?clubExamId=${clubExamId}`;
        },
    },
};
</script>

<style scoped lang="scss">
.noPointer {
    pointer-events: none;
}
.addButton {
    float: right;
}
</style>
